import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  executeAnyMutation,
  fetchJson,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Header from "../../../../components/Header";
import { loanManagementUrl } from "../../../../config";
import { useNavigate } from "react-router-dom";

const loan_url = loanManagementUrl.uri;

const validationSchema = yup.object().shape({
  PenaltyAmount: yup.number().required("Penalty Amount is required"),
  PenaltyReason: yup.string().required("Penalty Reason is required"),
});

const LoanPenaltyApplicationForm = (props) => {
  const navigate = useNavigate();
  const [loanId, setLoanId] = useState("");
  const [checking, setChecking] = useState(false);
  const [loanFound, setLoanFound] = useState(false);

  const checkLoan = async (loanIdValue) => {
    if (!loanIdValue) return;
    setChecking(true);
    try {
      const LoanChecker = await fetchJson({
        objectName: "disbursementViewBySpecificColumn",
        inputObjectName: "disbursementView",
        params: { loanId: loanIdValue },
        baseUrl: loan_url,
      });

      if (LoanChecker?.results?.length > 0) {
        setLoanFound(true);
      } else {
        setLoanFound(false);
        Swal.fire({
          icon: "error",
          title: "Not Found",
          text: `Loan ID "${loanIdValue}" does not exist.`,
        });
      }
    } catch {
      setLoanFound(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to verify Loan ID. Please try again.",
      });
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutaionDataEditing = {
        loanid: loanId,
        penaltyReason: values.PenaltyReason,
        penaltyAmount: parseFloat(values.PenaltyAmount),
      };

      const response = await executeAnyMutation(
        "insertLoanPenalty",
        mutaionDataEditing,
        "penaltyApplication",
        false,
        loanManagementUrl.uri
      );

      if (response.results) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Loan Penalty applied successfully.",
        }).then(() => {
          navigate("/approve-loan-penalty");
        });
      } else if (response.error) {
        throw new Error(response.error);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header title="Apply Loan Penalty" subtitle="Apply Loan Penalty" />

      {/* Loan ID lookup field */}
      <Box mb="30px">
        <TextField
          fullWidth
          variant="filled"
          label="Loan ID *"
          value={loanId}
          onChange={(e) => {
            setLoanId(e.target.value);
            setLoanFound(false);
          }}
          onBlur={() => checkLoan(loanId)}
          disabled={checking}
          InputProps={{
            endAdornment: checking ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      <Formik
        initialValues={{ PenaltyAmount: "", PenaltyReason: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns={{
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              }}
            >
              <Box sx={{ gridColumn: "span 2" }}>
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Penalty Amount *"
                  name="PenaltyAmount"
                  value={values.PenaltyAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!loanFound}
                  error={!!touched.PenaltyAmount && !!errors.PenaltyAmount}
                  helperText={touched.PenaltyAmount && errors.PenaltyAmount}
                />
              </Box>
              <Box sx={{ gridColumn: "span 4" }}>
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Penalty Reason *"
                  name="PenaltyReason"
                  value={values.PenaltyReason}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!loanFound}
                  error={!!touched.PenaltyReason && !!errors.PenaltyReason}
                  helperText={touched.PenaltyReason && errors.PenaltyReason}
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting || !loanFound}
              >
                Submit
              </Button>
            </Box>
            {isSubmitting && <CircularProgress />}
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default LoanPenaltyApplicationForm;
